import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { TeamMember } from '@/types/project';

interface ProjectTeamProps {
  projectId: string;
}

export default function ProjectTeam({ projectId }: ProjectTeamProps) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    role: '',
    category: 'takım' as TeamMember['category']
  });

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const { data, error: fetchError } = await supabase
        .from('project_team')
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: true });

      if (fetchError) {
        if (fetchError.code === '42P01') {
            setError('Veritabanı tablosu henüz oluşturulmamış. Lütfen SQL kodunu çalıştırın.');
        } else {
            setError('Ekip üyeleri yüklenirken bir hata oluştu');
        }
        setLoading(false);
        return;
      }

      setMembers(data || []);
    } catch {
      setError('Bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchMembers();
  }, [fetchMembers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.full_name.trim()) return;

    setSaving(true);
    setError('');

    try {
      if (editingMember) {
        const { error: updateError } = await supabase
          .from('project_team')
          .update({
            full_name: formData.full_name.trim(),
            email: formData.email.trim() || null,
            phone: formData.phone.trim() || null,
            role: formData.role.trim() || null,
            category: formData.category
          })
          .eq('id', editingMember.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('project_team')
          .insert({
            project_id: projectId,
            full_name: formData.full_name.trim(),
            email: formData.email.trim() || null,
            phone: formData.phone.trim() || null,
            role: formData.role.trim() || null,
            category: formData.category
          });

        if (insertError) throw insertError;
      }

      await fetchMembers();
      closeForm();
    } catch {
      setError('Kişi kaydedilirken bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const deleteMember = async (id: string) => {
    if (!confirm('Bu kişiyi ekipten çıkarmak istediğinize emin misiniz?')) return;

    const { error: deleteError } = await supabase
      .from('project_team')
      .delete()
      .eq('id', id);

    if (deleteError) {
      setError('Kişi silinirken bir hata oluştu');
      return;
    }

    setMembers(members.filter((m) => m.id !== id));
  };

  const openEdit = (member: TeamMember) => {
    setEditingMember(member);
    setFormData({
      full_name: member.full_name,
      email: member.email || '',
      phone: member.phone || '',
      role: member.role || '',
      category: member.category
    });
    setShowAddForm(true);
  };

  const closeForm = () => {
    setShowAddForm(false);
    setEditingMember(null);
    setFormData({ full_name: '', email: '', phone: '', role: '', category: 'takım' });
  };

  if (loading && members.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-stone-300 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-stone-800">Proje Kişileri</h2>
          <p className="text-xs text-stone-400 mt-0.5">
            {members.length} kişi kayıtlı
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-3 py-2 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-user-add-line"></i>
          Kişi Ekle
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl p-5 border border-stone-200 space-y-4 shadow-md ring-2 ring-amber-500/10">
          <h3 className="font-bold text-stone-800">{editingMember ? 'Kişiyi Düzenle' : 'Yeni Kişi Ekle'}</h3>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Ad Soyad</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                required
                disabled={saving}
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50"
                placeholder="Örn: Ahmet Yılmaz"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Kategori</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as TeamMember['category'] })}
                disabled={saving}
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50 cursor-pointer"
              >
                <option value="takım">Takım</option>
                <option value="tedarikçi">Tedarikçi</option>
                <option value="bilirkişi">Bilirkişi</option>
                <option value="müşteri">Müşteri</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Görev / Rol</label>
              <input
                type="text"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                disabled={saving}
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50"
                placeholder="Örn: Mimar, Usta, Elektrikçi"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">E-posta</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                disabled={saving}
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50"
                placeholder="ornek@mail.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1">Telefon</label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                disabled={saving}
                className="w-full px-4 py-2 rounded-lg border border-stone-300 text-stone-800 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all disabled:opacity-50"
                placeholder="05xx xxx xx xx"
              />
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="flex-1 py-2.5 border border-stone-300 text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 py-2.5 bg-stone-800 text-white rounded-lg text-sm font-medium hover:bg-stone-900 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
            >
              {saving ? 'Kaydediliyor...' : (editingMember ? 'Güncelle' : 'Ekle')}
            </button>
          </div>
        </form>
      )}

      {/* Members List - Table Format */}
      {members.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-stone-300">
          <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <i className="ri-group-line text-stone-400 text-xl"></i>
          </div>
          <p className="text-stone-400 text-sm">Henüz kişi bulunmuyor</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-100">
                  <th className="px-5 py-3 text-xs font-bold text-stone-500 uppercase tracking-wider">Ad Soyad</th>
                  <th className="px-5 py-3 text-xs font-bold text-stone-500 uppercase tracking-wider">Kategori</th>
                  <th className="px-5 py-3 text-xs font-bold text-stone-500 uppercase tracking-wider">Görev / Rol</th>
                  <th className="px-5 py-3 text-xs font-bold text-stone-500 uppercase tracking-wider">İletişim</th>
                  <th className="px-5 py-3 text-xs font-bold text-stone-500 uppercase tracking-wider text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-50">
                {members.map((member) => (
                  <tr key={member.id} className="hover:bg-amber-50/30 transition-colors group">
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 font-bold text-xs">
                          {member.full_name.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-stone-800">{member.full_name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                        member.category === 'takım' ? 'bg-blue-100 text-blue-700' :
                        member.category === 'tedarikçi' ? 'bg-amber-100 text-amber-700' :
                        member.category === 'bilirkişi' ? 'bg-purple-100 text-purple-700' :
                        'bg-stone-100 text-stone-700'
                      }`}>
                        {member.category}
                      </span>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-sm text-stone-600">
                      {member.role || '-'}
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {member.phone && (
                          <a href={`tel:${member.phone}`} className="text-xs text-stone-500 hover:text-amber-600 flex items-center gap-1">
                            <i className="ri-phone-line"></i> {member.phone}
                          </a>
                        )}
                        {member.email && (
                          <a href={`mailto:${member.email}`} className="text-xs text-stone-500 hover:text-amber-600 flex items-center gap-1">
                            <i className="ri-mail-line"></i> {member.email}
                          </a>
                        )}
                        {!member.phone && !member.email && <span className="text-xs text-stone-300">-</span>}
                      </div>
                    </td>
                    <td className="px-5 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openEdit(member)}
                          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors cursor-pointer"
                          title="Düzenle"
                        >
                          <i className="ri-edit-line"></i>
                        </button>
                        <button
                          onClick={() => deleteMember(member.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors cursor-pointer"
                          title="Sil"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
